import { useEffect, useRef } from 'react';

/**
 * Reveals children on scroll.
 *
 * Attach the returned ref to any container; every descendant carrying the
 * `.reveal` class fades and rises into place once it enters the viewport, with a
 * small stagger so a row of cards arrives as a sequence rather than a block.
 *
 * Uses IntersectionObserver (no scroll listeners, so it stays smooth) and
 * unobserves each element after it fires - the animation plays once.
 */
export function useReveal({ stagger = 90, threshold = 0.12, rootMargin = '0px 0px -8% 0px' } = {}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const items = Array.from(root.querySelectorAll('.reveal'));
    if (!items.length) return;

    // Respect the user's motion preference: show everything immediately.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Stagger within the batch that became visible together.
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry, i) => {
            const el = entry.target;
            const delay = Number(el.dataset.revealDelay ?? i * stagger);
            el.style.transitionDelay = `${delay}ms`;
            el.classList.add('is-visible');
            observer.unobserve(el);
          });
      },
      { threshold, rootMargin }
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [stagger, threshold, rootMargin]);

  return containerRef;
}

export default useReveal;
